/**
 * useTrackCourseVisit Hook
 *
 * Tracks when a user visits a course page.
 */

import { useCallback, useRef } from "react";
import { useMutation } from "urql";
import { useAuth } from "../../../lib/auth.tsx";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const TrackCourseVisitDocument = `
  mutation TrackCourseVisit($courseId: ID!) {
    trackCourseVisit(courseId: $courseId)
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface UseTrackCourseVisitResult {
  trackVisit: (courseId: string) => void;
  isLoading: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Track a course visit for analytics.
 * Fire-and-forget, only tracks once per courseId per mount.
 * Only tracks for authenticated users.
 */
export function useTrackCourseVisit(): UseTrackCourseVisitResult {
  const { isAuthenticated } = useAuth();
  const [result, executeMutation] = useMutation<{ trackCourseVisit: boolean }>(
    TrackCourseVisitDocument
  );
  const trackedRef = useRef<Set<string>>(new Set());

  const trackVisit = useCallback((courseId: string): void => {
    // Skip tracking for unauthenticated users
    if (!isAuthenticated) return;
    if (trackedRef.current.has(courseId)) return;
    trackedRef.current.add(courseId);
    executeMutation({ courseId });
  }, [executeMutation, isAuthenticated]);

  return {
    trackVisit,
    isLoading: result.fetching,
  };
}
